import type { GridRanks } from '@nsb/core';
import type { TileLine, TilePhase, TileStep } from './model.ts';

/**
 * Phase transitions as every tree draws them (design studies 20 and 21, as Jason answered them, 2026-10-07; the plan
 * `docs/plans/2026-10-07-transitions-working-back.md`, items 3 and 6). A transition is the band between two phases, and
 * one gate opens the phase below it: the gate that closes the phase above (Project Setup opens the first). Drawn, the
 * gate docks in the band (21: drawn B), centred between the end line of the phase it closes and the start line of the
 * one it opens, the next phase's title beside it just above that line (P-17, Jason, 2026-10-09: "very clearly showing
 * that the phase transiton nodes are the only way to get between phases"); the steps that start the phase hang from it.
 * A line from a step in one phase to a step in a later one is not drawn across a phase's lines: it runs through the
 * transitions (21: crossing A, "focusing on a step node shows the whole chain going through the gates"). Drawing only:
 * the tree's waits are never changed here.
 *
 * A line to a step only offered, or to one left out, runs through the transitions as a line to a step does (item 3:
 * "No other line is drawn across a phase heading"), dashed as it was; a line from one, and a decision's option lines,
 * are drawn as before; a phase whose gate is not on the job draws its heading as before, and a line across it as before.
 *
 * The one line that crosses a transition is a late step's (design study 21: "A late step’s lines are the only ones that
 * cross a transition, drawn in bengara"): a step come late sits in the phase under way, outside its own, and its lines
 * to and from steps of earlier phases (what it needs, back there)
 * are drawn straight across the headings between, never through the gates it went past (`isLateLine`).
 */

type Phases = readonly Pick<TilePhase, 'key' | 'closedBy'>[];

/** A step that is the job's (or the tree's), not one only offered or left out. */
const isReal = (s: TileStep | undefined): s is TileStep => Boolean(s && !s.ghost && !s.dot);

/**
 * Where each step comes in the run of phases: the start before the first (-1), a step at its phase's place, the end
 * after the last; a step in no phase has none. `all` counts what is only offered or left out too (where a line leaves
 * from); without it, only the real steps (what a line may run through the transitions between).
 */
export function placesOf(phases: Phases, steps: readonly TileStep[], all = false): Map<string, number> {
  const at = new Map(phases.map((p, i) => [p.key, i]));
  const out = new Map<string, number>();
  for (const s of steps) {
    if (!all && !isReal(s)) continue;
    if (s.end === 'start') out.set(s.key, -1);
    else if (s.end === 'end') out.set(s.key, phases.length);
    else if (s.phase !== null && at.has(s.phase)) out.set(s.key, at.get(s.phase)!);
  }
  return out;
}

/**
 * The step docked on each phase's heading, by the phase's key (21: drawn B): the start on the first phase drawn, and a
 * phase's closing gate on the next phase drawn, when it is alone at its phase's foot: on the job, or only offered
 * (P-15, Jason, 2026-10-09: "a phase transition node should always be locked to the top of the next phase like when it
 * is added for display porposes"), never a filed one's dot. A phase whose gate is not drawn, or with steps after its
 * gate, docks nothing. Docked only offered, it is placed there alone: no line runs through it (`Transit.gate` holds
 * the real steps' gates).
 */
export function docksOf(phases: Phases, rows: GridRanks, steps: readonly TileStep[]): Map<string, string> {
  const byKey = new Map(steps.map((s) => [s.key, s]));
  const on = new Map<string, string>();
  const groups = rows.groups;
  const first = groups[0];
  if (rows.start && first && first.phase !== null && isReal(byKey.get(rows.start))) on.set(first.phase, rows.start);
  groups.forEach((g, i) => {
    const next = groups[i + 1];
    if (g.phase === null || !next || next.phase === null) return;
    const closer = phases.find((p) => p.key === g.phase)?.closedBy ?? null;
    const last = g.rows.at(-1);
    const c = closer ? byKey.get(closer) : undefined;
    if (closer && last?.length === 1 && last[0] === closer && c && !c.dot && !c.end) on.set(next.phase, closer);
  });
  return on;
}

/** What the board knows of its transitions: each step's place, and the gate docked after each place. */
export interface Transit {
  /** The real steps' places (`placesOf`). */
  place: Map<string, number>;
  /** Every step's place, offered and left out too. */
  rank: Map<string, number>;
  /** The gate docked between its phase and the next, by the place it closes (the start at -1). */
  gate: Map<number, string>;
  /** The steps come late on the job, each sitting in a phase after its own (`TileStep.late`). */
  late: ReadonlySet<string>;
}

/**
 * A late step's line back to an earlier phase (design study 21): between a step come late and a step of a phase before
 * the one it landed in, offered and left out counted where they are drawn. Drawn straight across the headings between,
 * in bengara; never run through the transitions.
 */
export function isLateLine(l: Pick<TileLine, 'src' | 'dst'>, transit: Pick<Transit, 'rank' | 'late'>): boolean {
  const a = transit.rank.get(l.src);
  const b = transit.rank.get(l.dst);
  if (a === undefined || b === undefined || a === b) return false;
  return (transit.late.has(l.src) && b < a) || (transit.late.has(l.dst) && a < b);
}

/**
 * The lines the board draws, through the transitions (item 6): a line from a step to a step in a later phase is drawn
 * from the latest gate docked before the later step to it, and the step it came from, left
 * with nothing leaving it, is drawn into its own phase's gate, the gate that holds its whole phase (as the rows already
 * place it). The later step may be one only offered or left out (its line stays dashed, the board's); a line from
 * one of those, an option's line and a line whose own phase's gate is not docked are drawn as they are; a late step's
 * line back to an earlier phase is drawn as it is, marked late (`isLateLine`). Each line once.
 */
export function linesThrough(lines: readonly TileLine[], transit: Transit): TileLine[] {
  const out: TileLine[] = [];
  const own = new Map<string, string>();
  for (const l of lines) {
    if (isLateLine(l, transit)) {
      out.push({ ...l, late: true });
      continue;
    }
    // From a step the job (or the tree) has; to any step drawn, offered and left out too.
    const a = transit.place.get(l.src);
    const b = transit.rank.get(l.dst);
    const mine = a === undefined ? undefined : transit.gate.get(a);
    if (l.option !== null || a === undefined || b === undefined || a >= b || mine === undefined) {
      out.push(l);
      continue;
    }
    let k = b - 1;
    while (k > a && !transit.gate.has(k)) k--;
    const g = transit.gate.get(k)!;
    if (g === l.src) {
      out.push(l);
      continue;
    }
    out.push({ src: g, dst: l.dst, option: null });
    if (mine !== l.src) own.set(l.src, mine);
  }
  for (const [src, gate] of own) if (!out.some((l) => l.src === src)) out.push({ src, dst: gate, option: null });
  const seen = new Set<string>();
  return out.filter((l) => {
    const id = `${l.dst}<${l.src}${l.option ? `:${l.option}` : ''}`;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

/** A wait in a step's chain: a real one carries its line (to take it out); one through a gate carries none. */
export interface ChainEdge {
  src: string;
  dst: string;
  /** The wait it stands for, when the tree has it as a line; none for a leg through a gate. */
  line: TileLine | null;
  /** A leg through a gate between phases, drawn though the tree has no such line. */
  through: boolean;
}

/**
 * A step's whole chain, through the gates (item 6; Jason: "focusing on a step node shows the whole chain going through
 * the gates"): its real waits all the way back and all the way on, and where a wait is on a step of an earlier phase,
 * the gates between drawn in it, each phase's gate on the way (the start before the first). A gate passed through
 * brings only the way through it, not its own waits (the step's real needs, not the whole phase). `steps` are the
 * steps the chain is drawn from; `lines` the tree's lines, by which a real wait is known.
 */
export function chainThrough(steps: readonly TileStep[], lines: readonly TileLine[], phases: Phases, focus: string): { nodes: { key: string; needs: string[] }[]; edges: ChainEdge[] } {
  const byKey = new Map(steps.map((s) => [s.key, s]));
  if (!byKey.has(focus)) return { nodes: [], edges: [] };
  const place = placesOf(phases, steps);
  const gate = new Map<number, string>();
  for (const s of steps) if (s.end === 'start' && place.has(s.key)) gate.set(-1, s.key);
  phases.forEach((p, i) => {
    const c = p.closedBy ? byKey.get(p.closedBy) : undefined;
    if (isReal(c) && !c.end && c.phase === p.key) gate.set(i, c.key);
  });
  const lineOf = new Map(lines.map((l) => [`${l.dst}<${l.src}`, l]));
  // Come late: its line back to an earlier phase went past the gates between, so the chain draws it straight, as the board does.
  const late = new Set(steps.filter((s) => s.late).map((s) => s.key));
  const rank = placesOf(phases, steps, true);
  // Each step's waits, a decision's option marked (its line stays as it is).
  const needsOf = new Map<string, { src: string; option: boolean }[]>();
  const dependents = new Map<string, string[]>();
  for (const s of steps) {
    const ns: { src: string; option: boolean }[] = [];
    for (const n of s.needs) {
      const option = !byKey.has(n) && n.lastIndexOf(':') > 0;
      const k = option ? n.slice(0, n.lastIndexOf(':')) : n;
      if (!byKey.has(k) || k === s.key || ns.some((x) => x.src === k)) continue;
      ns.push({ src: k, option });
      dependents.set(k, [...(dependents.get(k) ?? []), s.key]);
    }
    needsOf.set(s.key, ns);
  }
  const edges = new Map<string, ChainEdge>();
  const add = (src: string, dst: string, through: boolean) => {
    const id = `${dst}<${src}`;
    const had = edges.get(id);
    if (had) {
      if (!through) had.through = false;
      return;
    }
    edges.set(id, { src, dst, line: lineOf.get(id) ?? null, through });
  };
  /** A wait, through the gates of the phases between: from the gate of its own phase to the gate of the phase before. */
  const wait = (src: string, dst: string, option: boolean) => {
    const a = place.get(src);
    const b = place.get(dst);
    const gs: string[] = [];
    if (!option && a !== undefined && b !== undefined && a < b && !isLateLine({ src, dst }, { rank, late })) {
      for (let k = a; k < b; k++) {
        const g = gate.get(k);
        if (g !== undefined && g !== src && g !== dst) gs.push(g);
      }
    }
    if (!gs.length) return add(src, dst, false);
    const seq = [src, ...gs, dst];
    for (let i = 1; i < seq.length; i++) add(seq[i - 1]!, seq[i]!, true);
  };
  const walk = (next: (k: string) => { key: string; option: boolean }[], join: (from: string, to: string, option: boolean) => void) => {
    const seen = new Set([focus]);
    const queue = [focus];
    while (queue.length) {
      const k = queue.shift()!;
      for (const n of next(k)) {
        join(k, n.key, n.option);
        if (!seen.has(n.key)) {
          seen.add(n.key);
          queue.push(n.key);
        }
      }
    }
  };
  walk(
    (k) => (needsOf.get(k) ?? []).map((n) => ({ key: n.src, option: n.option })),
    (k, need, option) => wait(need, k, option),
  );
  walk(
    (k) => (dependents.get(k) ?? []).map((z) => ({ key: z, option: Boolean(needsOf.get(z)?.find((n) => n.src === k)?.option) })),
    (k, z, option) => wait(k, z, option),
  );
  const into = new Map<string, string[]>();
  for (const e of edges.values()) into.set(e.dst, [...(into.get(e.dst) ?? []), e.src]);
  const inChain = new Set([focus, ...[...edges.values()].flatMap((e) => [e.src, e.dst])]);
  return {
    nodes: steps.filter((s) => inChain.has(s.key)).map((s) => ({ key: s.key, needs: into.get(s.key) ?? [] })),
    edges: [...edges.values()],
  };
}
